import { KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CustomDataColumn } from '../models/custom-viewer-config';
import { IntegrationServiceImpl } from 'integration-service/integration-service-impl';
import { SMTDataListsService } from './data-lists.service';
import { KeyValueExtended } from '../models/key-value-extended';

@Injectable({
  providedIn: 'root'
})
export class CustomListService {

  //Guardar todos los elementos de todos los datalists que cargan en la vista
  public dataListsElements = new BehaviorSubject<{[datalistName: string]: KeyValue<string, string>[]}>({});

  //Control de tabla
  public reloadTable = new BehaviorSubject<any>(undefined);
  public showHideFiltersHeaderTable = new Subject<any>();
  public loadElements = new Subject<any>();

  public dataLength: number;
  public totalItems: number = 0;

  private dataListRequestsCache = new Map<string, Promise<any[]>>();

  constructor(private dataListsService: SMTDataListsService, private integrationService: IntegrationServiceImpl) {}

  /**
   * Solicita la recarga de la tabla de documentos.
   *
   * Este método emite un evento indicando que la tabla debe refrescarse. Puede
   * opcionalmente reiniciar los filtros si `resetFilters` es true. Es utilizado
   * por componentes que necesitan forzar una actualización del listado.
   *
   * @param resetFilters Indica si deben reiniciarse los filtros antes de recargar.
   */
  fireReloadTable(resetFilters: boolean, clearState?: boolean) {
    this.reloadTable.next({  resetFilters, clearState });
  }

  fireLoadDataListElements(reset: boolean, resetOnlyDynamic?: boolean) {
    this.loadElements.next({  reset, resetOnlyDynamic });
  }

  /**
   * Alterna la visibilidad del encabezado de filtros de la tabla.
   *
   * Este método emite un evento para mostrar u ocultar la sección de filtros
   * en la cabecera de la tabla de documentos. Se utiliza para controlar la UI
   * sin necesidad de acoplar directamente los componentes.
   */
  fireShowHideFiltersHeaderTable() {
    this.showHideFiltersHeaderTable.next();
  }

  /**
   * Carga los elementos de los Data Lists asociados a las columnas configuradas.
   * 
   * Para cada columna que tenga definido un dataListName, se obtiene su listado de valores desde Alfresco o desde un servicio de integración externo, 
   * según la configuración de la columna. Los resultados se almacenan internamente para
   * ser utilizados en filtros, selectores u otros componentes dependientes.
   * 
   * - Si la columna utiliza un Data List estándar, se consulta directamente a Alfresco.
   * - Si la columna está marcada como custom, se obtienen los valores desde el servicio de integración correspondiente.
   * - Si la columna utiliza el selector type-list (LEGACY), se recuperan los tipos de documento del modelo anterior y se transforman al formato clave-valor.
   * 
   * El parámetro reset permite invalidar la caché de las peticiones existentes para forzar la recarga de los Data Lists.
   * 
   * Si resetOnlyDynamic está activo junto con reset, únicamente se invalidan las entradas de caché correspondientes a columnas cuyo atributo dynamic sea true. 
   * Las listas de las columnas estáticas mantienen su caché y no se vuelven a solicitar.
   * 
   * En caso de error al obtener los datos, se registra en consola y se almacena una lista vacía para evitar fallos posteriores.
   * 
   * @param columns Lista de columnas configuradas para la tabla.
   * @param reset Si es true, invalida la caché de las peticiones para forzar la recarga de los Data Lists.
   * @param resetOnlyDynamic Si es true y reset también está activo, únicamente se invalidan las listas asociadas a columnas con dynamic === true.
  */
  loadDataListsElements(columns: CustomDataColumn[], reset?: boolean, resetOnlyDynamic?: boolean) {
    if (reset) {
      if (resetOnlyDynamic) {
        const dynamicCacheKeys = new Set(
          columns
            .filter(c => c.dynamic === true)
            .map(c =>
              [c.dataListName, c.parentKey, c.masterKey]
                .filter(v => v != null && v !== '')
                .join('|') || c.key
            )
        );

        for (const cacheKey of dynamicCacheKeys) {
          this.dataListRequestsCache.delete(cacheKey);
        }
      } else {
        this.dataListRequestsCache = new Map<string, Promise<any[]>>();
      }
    }

    for (const c of columns) {
      const cacheKey =
        [c.dataListName, c.parentKey, c.masterKey]
          .filter(v => v != null && v !== '')
          .join('|') || c.key;

      // Comprobamos si ya hemos hecho la petición para esta lista previamente
      if (this.dataListRequestsCache.has(cacheKey)) {
        this.dataListRequestsCache.get(cacheKey)
          ?.then(data => this.addDataListsElements(c.dataListName, data));
        continue;
      }

      let request: Promise<any[]> | undefined;

      // SMT LISTAS
      if (c.search.selector?.startsWith('smt-list')) {
        request = c.search.settings?.custom
          ? this.integrationService.getDataListElements(c.key, c.dataListName)
          : this.getAlfrescoDataListElements(c);

        // LEGACY LISTAS
      } else if (c.search.selector?.startsWith('type-list')) {
        request = this.getTypeList().then((res: any) =>
          (res || []).map(e => ({ key: e.id, value: e.labelId }))
        );
      }

      if (!request) continue;

      this.dataListRequestsCache.set(cacheKey, request);

      request
        .then(data => this.addDataListsElements(c.dataListName, data || []))
        .catch(err => {
          console.error(`Error obteniendo '${cacheKey}'`, err);
          this.addDataListsElements(c.dataListName, []);
        });
    }
  }

  getDataLengthFromTable() {
    return this.dataLength ?? 0;
  }

  /**
   * Devuelve el estado actual de los elementos cargados de Data Lists.
   *
   * Los elementos se almacenan en un BehaviorSubject como un diccionario donde
   * cada clave es el nombre del Data List y su valor es la lista de pares
   * clave‑valor asociados. Este método devuelve el valor actual sin necesidad
   * de suscripción.
   *
   * @returns Diccionario con los elementos de todos los Data Lists cargados.
   */
  private getDataListsElements(): { [datalistName: string]: KeyValue<string, string>[] } {
    return this.dataListsElements.value;
  }

  /**
   * Añade o actualiza los elementos asociados a un Data List concreto.
   *
   * Este método obtiene el estado actual de todos los Data Lists cargados,
   * actualiza la entrada correspondiente al `datalistName` y vuelve a emitir
   * el estado actualizado mediante `setDataListsElements`.
   *
   * @param datalistName Nombre del Data List a actualizar.
   * @param elements Lista de elementos en formato clave‑valor.
   */
  private addDataListsElements(datalistName: string, elements: KeyValue<string, string>[]) {
    const currentDataListsElements = this.getDataListsElements();
    currentDataListsElements[datalistName] = elements;
    this.setDataListsElements(currentDataListsElements);
  }

  /**
   * Actualiza el estado global de elementos de Data Lists.
   *
   * Este método emite un nuevo diccionario completo de Data Lists y sus
   * elementos asociados, permitiendo que cualquier suscriptor reaccione
   * automáticamente a los cambios.
   *
   * @param dataListsElements Diccionario actualizado de Data Lists.
   */
  private setDataListsElements(dataListsElements: { [datalistName: string]: KeyValue<string, string>[] }) {
    this.dataListsElements.next(dataListsElements);
  }

  /**
   * Obtiene los elementos de un Data List directamente desde Alfresco.
   *
   * Este método consulta el Data List indicado mediante `dataListsService`,
   * recibe los elementos en formato extendido y los ordena alfabéticamente
   * por su valor antes de devolverlos.
   *
   * @param datalistName Nombre del Data List a consultar.
   * @returns Promise con los elementos ordenados por su valor.
   */
  private getAlfrescoDataListElements(customDataColumn: CustomDataColumn): Promise<KeyValueExtended<string, string>[]> {
    const dataListName = customDataColumn.dataListName;
    const parentKey = customDataColumn.parentKey;
    const masterKey = customDataColumn.masterKey;
    return this.dataListsService.getDataListValues(dataListName, parentKey, masterKey).then((response: any) => {
        return response.sort((a, b) => a.value.localeCompare(b.value));
      });
  }

  /**
   * LEGACY
   * Método heredado para obtener la lista de tipos de documento previa
   * al refactor de Taxonomías de RRHH.
   *
   * Se mantiene únicamente por compatibilidad con funcionalidades antiguas.
   * En el modelo actual, los tipos de documento se obtienen desde las nuevas
   * taxonomías y no mediante este mecanismo legacy.
   */
   getTypeList(): Promise<any> {
    return this.dataListsService.getTypeList().then((response: any) => {
        return response.types.sort((a, b) => a.labelId.localeCompare(b.labelId));
      }).catch((err) => console.error(err));
  }
}
